import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { NerdBadge } from "../components/NerdBadge";
import articles from "../content/articles.json";

const NERD = { 1: "5th Grader", 2: "Chess Club", 3: "Full Pocket Protector" };

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "";

const linkProps = (a) =>
  a.type === "external"
    ? { as: "a", href: a.url, target: "_blank", rel: "noopener noreferrer" }
    : { as: "link", to: `/articles/${a.slug}` };

// Large, dramatic navy feature block for the newest piece.
function FeatureArticle({ a }) {
  const p = linkProps(a);
  const Wrapper = p.as === "a" ? "a" : Link;
  const wProps = p.as === "a" ? { href: p.href, target: p.target, rel: p.rel } : { to: p.to };
  return (
    <Wrapper
      {...wProps}
      aria-label={a.type === "external" ? `${a.title} — published in ${a.outlet} (opens in new tab)` : a.title}
      data-testid={`article-feature-${a.slug}`}
      className="group grain relative block overflow-hidden rounded-2xl bg-navy p-8 shadow-hero transition-transform duration-200 hover:-translate-y-[3px] focus:outline-none focus-visible:ring-2 focus-visible:ring-ochre focus-visible:ring-offset-2 md:p-12"
    >
      <div className="flex flex-wrap items-center gap-3">
        <NerdBadge difficulty={NERD[a.nerdScale]} />
        <span className="font-sans text-[12px] font-bold uppercase tracking-[0.16em] text-ochre-bright">
          {a.type === "external" ? `Published in ${a.outlet}` : "Latest"} · {fmtDate(a.date)}
        </span>
      </div>
      <h3 className="mt-6 max-w-3xl font-serif text-[32px] leading-[1.05] text-bone md:text-[46px]">{a.title}</h3>
      <p className="mt-4 max-w-2xl text-lg leading-relaxed text-on-navy-body">{a.excerpt}</p>
      <span className="mt-7 inline-flex items-center gap-2 font-sans text-[13px] font-bold uppercase tracking-[0.14em] text-ochre-bright">
        {a.type === "external" ? "Read externally" : "Read the piece"}
        {a.type === "external" ? (
          <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        ) : (
          <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
        )}
      </span>
    </Wrapper>
  );
}

// Editorial ruled row (magazine index feel) — no boxy card.
function ArticleRow({ a }) {
  const p = linkProps(a);
  const Wrapper = p.as === "a" ? "a" : Link;
  const wProps = p.as === "a" ? { href: p.href, target: p.target, rel: p.rel } : { to: p.to };
  return (
    <Wrapper
      {...wProps}
      aria-label={a.type === "external" ? `${a.title} — published in ${a.outlet} (opens in new tab)` : a.title}
      data-testid={a.type === "external" ? `article-card-external-${a.slug}` : `article-card-${a.slug}`}
      className="group grid gap-3 border-t border-navy/12 py-7 no-underline transition-colors hover:bg-navy/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-ochre md:grid-cols-[190px_1fr] md:gap-10 md:py-8"
    >
      <div className="flex flex-row items-center gap-3 md:flex-col md:items-start md:gap-3">
        <NerdBadge difficulty={NERD[a.nerdScale]} />
        <span className="font-sans text-[13px] text-text-muted">
          {a.type === "external" ? (
            <span className="font-serif italic">In {a.outlet}</span>
          ) : (
            fmtDate(a.date)
          )}
        </span>
      </div>
      <div>
        <h3 className="font-serif text-2xl leading-snug text-navy transition-colors group-hover:text-ochre md:text-[28px]">
          {a.title}
          {a.type === "external" && <ArrowUpRight size={18} className="ml-1 inline -translate-y-0.5" />}
        </h3>
        <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-text-muted">{a.excerpt}</p>
        <span className="mt-3 inline-block font-sans text-[12px] font-bold uppercase tracking-[0.12em] text-ochre">
          {a.type === "external" ? "Read externally →" : "Read →"}
        </span>
      </div>
    </Wrapper>
  );
}

export default function Articles() {
  useSeo({
    title: "Articles — Practical AI for Everyday Life",
    description:
      "Essays on artificial intelligence for the curious and the skeptical — what genuinely matters, what's noise, and what I've learned along the way. Every piece graded on the Nerd Scale.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://practicalaiforeverydaylife.com/" },
        { "@type": "ListItem", position: 2, name: "Articles", item: "https://practicalaiforeverydaylife.com/articles" },
      ],
    },
  });

  const posts = articles.filter((a) => a.type === "post");
  const feature = posts[0] || articles[0];
  const rest = articles.filter((a) => a !== feature);
  const hasAny = articles.length > 0;

  return (
    <div>
      {/* HERO */}
      <section className="on-navy bg-navy grain" data-testid="articles-hero">
        <div className="container-brand flex flex-col items-center py-20 text-center md:py-24">
          <p className="eyebrow !text-ochre-bright">Articles</p>
          <h1 className="mt-4 max-w-3xl font-serif text-5xl leading-[1.05] text-bone md:text-6xl">
            The stuff worth <span className="italic text-ochre-bright">understanding.</span>
          </h1>
          <p className="mt-6 max-w-measure text-lg text-on-navy-body">
            Essays on artificial intelligence for the curious and the skeptical — distinguishing what genuinely
            matters from the noise, and thinking through what I've learned (and broken) along the way. Every piece
            is graded on the Nerd Scale, so you'll know precisely how technical it gets before you begin.
          </p>
        </div>
      </section>

      {/* MAIN FEED — asymmetric: feature + editorial rows */}
      <section className="bg-warm grain" data-testid="articles-feed">
        <div className="container-brand py-16 md:py-20">
          <p className="eyebrow">The Full Archive</p>
          {hasAny ? (
            <>
              {feature && (
                <div className="mt-6">
                  <FeatureArticle a={feature} />
                </div>
              )}
              {rest.length > 0 && (
                <div className="mt-12 border-b border-navy/12">
                  {rest.map((a) => (
                    <ArticleRow key={a.type + "-" + a.slug} a={a} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="mt-6 font-serif text-2xl text-navy" data-testid="articles-empty">
              Nothing published yet. It's coming.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
